import { Button } from "@meiden-connect/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  isMcpToolVisible,
  MCP_TOOLS_OVERVIEW,
  type McpCanvasPermissions,
  type McpToolPermission,
} from "@refinement-canvas/domain";
import { getWebConfig } from "../../env";
import { useCanvasApi } from "../../lib/canvas-api";
import { navigate } from "../../router";

// MCP（リモート Model Context Protocol）連携の設定手順ページ。
// AI エージェント（Claude Code / VS Code の Codex / Claude.ai / Cursor 等）からキャンバスを検索・分析・編集する
// ための接続情報と手順を案内する。

/** 接続案内に表示する MCP の接続情報。 */
export interface McpConnectionInfo {
  /** MCP のエンドポイント URL（`<サービスのオリジン>/mcp`）。 */
  readonly url: string;
  /**
   * OAuth で要求するスコープ（空白区切り）。API の Protected Resource Metadata と 401 チャレンジが案内する
   * canonical の集合（openid / offline_access / サービス URL の host）と同じ値にする。権限 ID（canvas-read など）は
   * 要求しない（一部の権限だけを持つ利用者の認可が invalid_scope で失敗するため）。
   */
  readonly scope: string;
}

/** https 以外で接続先として案内するループバックのホスト名（ローカル開発用。API の検証と同じ）。 */
const MCP_LOOPBACK_HOSTNAMES = new Set(["localhost", "127.0.0.1", "[::1]"]);

/**
 * MCP の接続情報を組み立てる。公開 URL は runtime config の serviceOrigin を使い、無いときは mock 認証の
 * ローカル開発に限って表示中のオリジンを使う（認証必須の環境で別のホスト名から開いた画面に、誤った URL を
 * 案内しないため）。オリジンが無いとき、オリジンそのもの（末尾スラッシュを除く。パス・クエリなどを含まない）で
 * ないとき、https でもループバックの http でもないときは undefined を返し、画面は接続の手順の代わりに設定の
 * 確認を案内する（不正な設定で画面ごと表示できなくならないようにする。判定は API の Protected Resource
 * Metadata のオリジンの検証〈apps/api/src/mcp/auth-exchange.ts の parseMcpOauthOrigin〉と同じ基準）。
 */
export function buildMcpConnectionInfo(
  serviceOrigin: string | undefined,
  fallbackOrigin: string,
  authMode: "mock" | "required"
): McpConnectionInfo | undefined {
  const rawOrigin = serviceOrigin ?? (authMode === "mock" ? fallbackOrigin : undefined);
  if (rawOrigin === undefined) {
    return undefined;
  }
  const origin = rawOrigin.replace(/\/+$/u, "");
  let host: string;
  try {
    const url = new URL(origin);
    const isAllowedScheme =
      url.protocol === "https:" ||
      (url.protocol === "http:" && MCP_LOOPBACK_HOSTNAMES.has(url.hostname));
    if (!isAllowedScheme || url.origin !== origin) {
      return undefined;
    }
    host = url.host;
  } catch {
    return undefined;
  }
  return {
    url: `${origin}/mcp`,
    scope: ["openid", "offline_access", host].join(" "),
  };
}

const PERMISSION_LABEL: Record<McpToolPermission, string> = {
  any: "全員",
  read: "閲覧",
  write: "編集",
  admin: "管理",
};

export function McpSetupView(): JSX.Element {
  const connection = useMemo(() => {
    const config = getWebConfig();
    return buildMcpConnectionInfo(config.serviceOrigin, window.location.origin, config.authMode);
  }, []);
  const canvasApi = useCanvasApi();

  const meQuery = useQuery({ queryKey: ["me"], queryFn: canvasApi.fetchMe });
  const permissions: McpCanvasPermissions = meQuery.data?.actor.permissions ?? {
    canRead: false,
    canWrite: false,
    canAdmin: false,
  };
  const visibleTools = MCP_TOOLS_OVERVIEW.filter((tool) => isMcpToolVisible(tool, permissions));

  return (
    <div className="dashboard-content" data-testid="mcp-setup-root">
      <section className="dashboard-hero-card mcp-hero">
        <div className="dashboard-hero-copy">
          <p className="dashboard-eyebrow">MCP 連携</p>
          <h1>AI からキャンバスを操作する（MCP 設定手順）</h1>
          {/* 日本語の文中で改行すると表示に空白が入るため、文中では改行しない。 */}
          <p>
            {"MCP（Model Context Protocol）対応の AI エージェント（Claude Code / VS Code の Codex / Claude.ai / Cursor など）を Connect Canvas に接続すると、"}
            {"自然言語で付箋・テキストを含むキャンバスの検索・分析や、新規キャンバスの作成・既存キャンバスへの図形コンテンツ追加が行えます。"}
          </p>
          <div>
            <Button type="button" variant="action" onClick={() => navigate("/")}>
              ← ダッシュボードへ戻る
            </Button>
          </div>
        </div>
      </section>

      {connection === undefined ? (
        <section className="dashboard-card" data-testid="mcp-setup-connection-unavailable">
          <h2>接続情報</h2>
          {/* 日本語の文中で改行すると表示に空白が入るため、1 つの文は 1 行に書く。 */}
          <p className="dashboard-muted" role="alert">
            {"接続情報を表示できません（サービスの公開 URL の設定が不足しているか、正しくありません）。"}
            {"管理者に設定の確認を依頼してください。"}
          </p>
        </section>
      ) : (
        <McpConnectionGuide connection={connection} />
      )}

      <section className="dashboard-card">
        <h2>利用できるツール</h2>
        {meQuery.isLoading ? (
          <p className="dashboard-muted">読み込み中…</p>
        ) : (
          <>
            <p className="dashboard-muted">
              現在のログイン権限（閲覧 {labelBool(permissions.canRead)} / 編集{" "}
              {labelBool(permissions.canWrite)} / 管理 {labelBool(permissions.canAdmin)}）で利用できる
              ツールの一覧です。
            </p>
            <table className="mcp-tools-table">
              <thead>
                <tr>
                  <th>ツール名</th>
                  <th>説明</th>
                  <th>必要権限</th>
                </tr>
              </thead>
              <tbody>
                {visibleTools.map((tool) => (
                  <tr key={tool.name}>
                    <td>
                      <code className="mcp-inline-code">{tool.name}</code>
                    </td>
                    <td>{tool.summary}</td>
                    <td>{PERMISSION_LABEL[tool.permission]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </section>

      <section className="dashboard-card">
        <h2>基本的な使い方の例</h2>
        <ol className="mcp-steps">
          <li>「最近のリファインメントのキャンバスから『在庫管理』に関するものを探して」→ search_canvases</li>
          <li>「そのキャンバスの付箋を要約して」→ get_canvas で内容を取得し AI が要約</li>
          <li>「スプリント計画用の新しいキャンバスを作って、優先度の付箋を3つ置いて」→ create_canvas</li>
          <li>「このキャンバスに『リスク』の付箋と、それを指す矢印を追加して」→ add_canvas_objects</li>
        </ol>
      </section>
    </div>
  );
}

/**
 * 公開 URL と OAuth スコープに依存する案内（接続情報・クライアントごとの手順・認証方式・接続できないとき）。
 * 接続情報を組み立てられないときは、参照先の無い案内や使えない認証方式を示さないよう、まとめて表示しない。
 */
function McpConnectionGuide({ connection }: { readonly connection: McpConnectionInfo }): JSX.Element {
  const { url: mcpUrl, scope: mcpScope } = connection;
  const claudeCodeCommand = `claude mcp add --transport http connect-canvas ${mcpUrl}`;
  const codexAddCommand = `codex mcp add connect-canvas --url ${mcpUrl}`;
  const codexLoginCommand = "codex mcp login connect-canvas";
  const clientConfigJson = JSON.stringify(
    {
      mcpServers: {
        "connect-canvas": {
          type: "streamable-http",
          url: mcpUrl,
          // OAuth 自動認証を使わない場合のみ、API キー / アクセストークンを指定する。
          headers: { Authorization: "Bearer YOUR_API_KEY_OR_ACCESS_TOKEN" },
        },
      },
    },
    null,
    2,
  );

  return (
    <>
      <section className="dashboard-card">
        <h2>接続情報</h2>
        <dl className="mcp-info-grid">
          <dt>エンドポイント URL</dt>
          <dd>
            <CopyableCode value={mcpUrl} />
          </dd>
          <dt>トランスポート</dt>
          <dd>Streamable HTTP（MCP プロトコル 2025-03-26）</dd>
          <dt>認証方式</dt>
          <dd>OAuth 認証（推奨）／ アクセストークン ／ API キー</dd>
          <dt>OAuth スコープ</dt>
          <dd>
            <CopyableCode value={mcpScope} />
            {/* 日本語の文中で改行すると表示に空白が入るため、1 つの文は 1 行に書く。 */}
            <p className="dashboard-muted">
              対応クライアントはこのスコープをサーバーから自動で取得して要求します。
              {"スコープを手動で指定する場合も、この値だけを使ってください（canvas-read などの権限名は指定しません）。"}
            </p>
          </dd>
        </dl>
        <p className="dashboard-muted mcp-note">
          ※ MCP 経由で追加・変更した内容は、すでにキャンバスを開いている画面には即時反映されません。
          反映を確認するにはブラウザでキャンバスを再読み込みしてください。
        </p>
      </section>

      <section className="dashboard-card" data-testid="mcp-setup-claude-code">
        <h2>Claude Code で接続する（OAuth 自動認証）</h2>
        <p>
          ターミナルで次のコマンドを実行してから、Claude Code の <code className="mcp-inline-code">/mcp</code>{" "}
          で connect-canvas を選んで認証（Authenticate）します。
        </p>
        <CopyableCode value={claudeCodeCommand} block />
        <p className="dashboard-muted">
          {"初回はブラウザが開き、MEIDEN CONNECT の認可画面が表示されます。許可するとトークンが自動で保存され、以降はトークンの期限が切れるまで再認証なしで利用できます。"}
          {"トークンの期限が切れたときは、Claude Code の "}
          <code className="mcp-inline-code">/mcp</code>
          {" で connect-canvas を選び、もう一度認証してください。"}
        </p>
      </section>

      <section className="dashboard-card" data-testid="mcp-setup-codex">
        <h2>Codex（VS Code の Codex 拡張・Codex CLI）で接続する（OAuth 自動認証）</h2>
        <p>
          {"ターミナルで次の 2 つのコマンドを順に実行し、ブラウザで MEIDEN CONNECT にログインします。"}
          {"codex コマンドが無い場合は、先に Codex CLI を導入してください。"}
        </p>
        <ol className="mcp-steps">
          <li>
            MCP サーバーを登録します。
            <CopyableCode value={codexAddCommand} block />
          </li>
          <li>
            OAuth で認証します（ブラウザで MEIDEN CONNECT の認可画面が開きます）。
            <CopyableCode value={codexLoginCommand} block />
          </li>
        </ol>
        <p className="dashboard-muted">
          VS Code の Codex 拡張は Codex CLI と同じ設定（ホームディレクトリの{" "}
          <code className="mcp-inline-code">.codex/config.toml</code>
          ）を使うため、登録と認証の後に VS Code の Codex を開き直すと connect-canvas を利用できます。
          {"認証が切れたときは 2 つ目のコマンドで認証し直してください。"}
        </p>
      </section>

      <section className="dashboard-card">
        <h2>Claude.ai（ブラウザ版）で接続する</h2>
        <ol className="mcp-steps">
          <li>Claude.ai の設定 →「コネクタ」→「カスタムコネクタを追加」を開きます。</li>
          <li>
            「名前」に <code className="mcp-inline-code">Connect Canvas</code>、「リモート MCP サーバーの
            URL」に上記のエンドポイント URL（
            <code className="mcp-inline-code">{mcpUrl}</code>）を入力します。
          </li>
          <li>追加後に「接続」を押すと MEIDEN CONNECT のログイン画面が開きます。許可すると接続完了です。</li>
        </ol>
      </section>

      <section className="dashboard-card">
        <h2>その他のクライアント（Claude Desktop / Cursor / GitHub Copilot 等）</h2>
        <p>
          設定ファイルに次の MCP サーバー定義を追加します。OAuth 自動認証に対応しないクライアントでは、
          <code className="mcp-inline-code">Authorization</code> ヘッダーに API キーまたはアクセストークンを
          設定してください。
        </p>
        <CopyableCode value={clientConfigJson} block />
      </section>

      <section className="dashboard-card">
        <h2>認証方式の選び方</h2>
        <ul className="mcp-auth-list">
          <li>
            <strong>OAuth 認証（推奨）</strong>
            {"：Claude Code / Codex / Claude.ai など対応クライアントは、URL を登録した後に上記の各クライアントの手順で認証すると MEIDEN CONNECT のログインに誘導され、トークン管理も自動です。"}
            {"利用できる操作は、MEIDEN CONNECT で付与されている Connect Canvas の権限（閲覧・編集・管理）の範囲に限られます。"}
          </li>
          <li>
            <strong>アクセストークン</strong>：短期間の動作確認向け。ブラウザの開発者ツールで取得した
            アクセストークンを <code className="mcp-inline-code">Authorization: Bearer &lt;token&gt;</code> に
            設定します（有効期限が切れると再取得が必要です）。
          </li>
          <li>
            <strong>API キー</strong>：長期運用向け。MEIDEN CONNECT で発行した API キーを Bearer に設定すると、
            サーバー側で短命アクセストークンへ自動交換します。
          </li>
        </ul>
      </section>

      <section className="dashboard-card" data-testid="mcp-setup-troubleshooting">
        <h2>接続できないとき</h2>
        <ul className="mcp-auth-list">
          <li>
            <strong>invalid_scope と表示される</strong>
            {"：以前の設定でスコープ（canvas-read など）を手動で指定している場合は、その指定を外してサーバーを登録し直し、もう一度認証してください。"}
            {"要求するスコープは上記の「OAuth スコープ」です。"}
          </li>
          <li>
            <strong>認証メタデータを読めない・resource が無いと表示される</strong>
            {"：登録したエンドポイント URL が上記の URL（末尾の "}
            <code className="mcp-inline-code">/mcp</code>
            {" まで）と一致しているかを確かめてください。"}
          </li>
          <li>
            <strong>問い合わせるとき</strong>
            {"：クライアントの名前とバージョン、表示されたエラーメッセージ、発生した日時だけを伝えてください。"}
            {"認可画面の URL・認可コード・アクセストークン・API キー・Cookie・設定ファイルの内容は、問い合わせや画面共有に含めないでください。"}
          </li>
        </ul>
      </section>
    </>
  );
}

function labelBool(value: boolean): string {
  return value ? "○" : "×";
}

/** コピー操作の結果（idle: 未操作・表示の戻し後）。 */
type CopyState = "idle" | "copied" | "failed";

/** クリップボードへコピーできるコード表示。block=true で複数行ブロック表示にする。 */
function CopyableCode({
  value,
  block,
}: {
  readonly value: string;
  readonly block?: boolean | undefined;
}): JSX.Element {
  const [copyState, setCopyState] = useState<CopyState>("idle");
  const handleCopy = (): void => {
    const clipboard = navigator.clipboard as Clipboard | undefined;
    if (clipboard === undefined) {
      // クリップボードを使えない環境（非セキュアな接続など）。手動でのコピーを案内する。
      setCopyState("failed");
      return;
    }
    void clipboard.writeText(value).then(
      () => {
        setCopyState("copied");
        window.setTimeout(() => setCopyState("idle"), 2000);
      },
      () => {
        // 権限の拒否などで書き込めないときも、手動でのコピーを案内する（案内文に値を繰り返さない）。
        setCopyState("failed");
      },
    );
  };
  return (
    <>
      <div className={block ? "mcp-code-block" : "mcp-code-inline-row"}>
        <code className={block ? "mcp-code-block-text" : "mcp-inline-code"}>{value}</code>
        <Button type="button" variant="action" onClick={handleCopy}>
          {copyState === "copied" ? "コピーしました" : "コピー"}
        </Button>
      </div>
      {/* コードとボタンの行（flex）の外に置き、ブロック表示のコードの幅を狭めない。 */}
      {copyState === "failed" ? (
        <p className="dashboard-muted" role="status">
          コピーできませんでした。表示されている文字列を選択してコピーしてください。
        </p>
      ) : null}
    </>
  );
}
